// src/config/env.ts
//
// Centralisation de toutes les variables d'environnement utilisées dans la SPA.
// Permet d'éviter les liens hardcodés dans le code applicatif et facilite la
// configuration par environnement (.env.development, .env.production, etc.).
//
// Pour ajouter une nouvelle variable :
//   1. Définir REACT_APP_XXX dans .env.development ET .env.production
//   2. L'exposer ici avec un fallback raisonnable pour le dev local
//   3. L'importer depuis ce module dans le code applicatif

/** URL absolue de l'API backend, ex : http://localhost:1920/api */
export const API_URL: string =
  process.env.REACT_APP_API_URL || 'http://localhost:1920/api';

/**
 * URL publique du frontend (SPA), incluant le basename éventuel.
 *  - en dev  : http://localhost:3000/gestioncaisse
 *  - en prod : https://sag-ecom.sag.ci/gestioncaisse
 *
 * Variable principale : REACT_APP_FRONT_URL
 * Rétrocompatibilité  : REACT_APP_PUBLIC_URL (ancien nom, accepté en fallback)
 *
 * Si aucune n'est définie, on dérive depuis `window.location` à l'exécution
 * (utile pour le SSR/build où window n'existe pas).
 */
export const FRONT_URL: string = (() => {
  if (process.env.REACT_APP_FRONT_URL)  return process.env.REACT_APP_FRONT_URL;
  if (process.env.REACT_APP_PUBLIC_URL) return process.env.REACT_APP_PUBLIC_URL;
  if (typeof window !== 'undefined') {
    const basename = process.env.REACT_APP_BASENAME ?? '/gestioncaisse';
    return `${window.location.protocol}//${window.location.host}${basename}`;
  }
  return 'http://localhost:3000';
})();

/** @deprecated Utilisez `FRONT_URL`. Alias conservé pour rétrocompatibilité. */
export const PUBLIC_URL: string = FRONT_URL;

/**
 * URL frontend par environnement — utilisée par le composant
 * EspaceClientPublicConfigTab pour permettre à l'admin de récupérer
 * en un clic les liens du portail pour PROD ou DEV.
 * Surchargeables via REACT_APP_FRONT_URL_PROD / REACT_APP_FRONT_URL_DEV
 * (rétrocompat : REACT_APP_PUBLIC_URL_PROD / REACT_APP_PUBLIC_URL_DEV).
 */
export const FRONT_URL_PROD: string =
  process.env.REACT_APP_FRONT_URL_PROD
  || process.env.REACT_APP_PUBLIC_URL_PROD
  || 'https://sag-ecom.sag.ci';

export const FRONT_URL_DEV: string =
  process.env.REACT_APP_FRONT_URL_DEV
  || process.env.REACT_APP_PUBLIC_URL_DEV
  || 'http://localhost:3001';
  

/** Basename de la SPA (route racine). Vide = servi à la racine /. */
export const BASENAME: string = process.env.REACT_APP_BASENAME ?? '/gestioncaisse';

/**
 * Préfixe à coller devant un chemin pour construire un lien.
 *
 * `BASENAME` vaut « / » sur les instances servies à la racine : le concaténer
 * produisait des adresses à double barre (https://app.sag.ci//portail/ffg),
 * affichées telles quelles à l'utilisateur.
 */
export const BASE_PATH: string = BASENAME === '/' ? '' : BASENAME.replace(/\/+$/, '');

/** URL du site corporate / commercial (lien externe en footer, etc.). */
export const SITE_CORPORATE_URL: string =
  process.env.REACT_APP_SITE_CORPORATE || 'https://www.sag.ci';

/** Nom de marque affiché dans l'UI (fallback header, titres, "Powered by"). */
export const BRAND_NAME: string =
  process.env.REACT_APP_BRAND_NAME || 'SAGeCom';

/** URL publique de la marque (lien "Powered by" dans les portails publics). */
export const BRAND_URL: string =
  process.env.REACT_APP_BRAND_URL || 'https://www.sag.ci';

// ─── Contact commercial (visible sur la landing, le footer, pages contact) ──

export const CONTACT_EMAIL: string =
  process.env.REACT_APP_CONTACT_EMAIL || 'kkadonga@yahoo.fr';

/** Numéro WhatsApp principal au format international (sans +, ni espaces). */
export const CONTACT_WHATSAPP_PRIMARY: string =
  process.env.REACT_APP_CONTACT_WHATSAPP || '2250749910887';

/** Numéro WhatsApp secondaire (commercial 2). */
export const CONTACT_WHATSAPP_SECONDARY: string =
  process.env.REACT_APP_CONTACT_WHATSAPP_2 || '2250777064397';

/** Numéro de téléphone principal au format international (sans espaces). */
export const CONTACT_PHONE_PRIMARY: string =
  process.env.REACT_APP_CONTACT_PHONE || '+2250749910887';

export const CONTACT_PHONE_SECONDARY: string =
  process.env.REACT_APP_CONTACT_PHONE_2 || '+2250777064397';

/** Helper : construit un lien wa.me avec message optionnel. */
export const whatsappUrl = (numero: string, message?: string): string => {
  const num = numero.replace(/[^0-9]/g, '');
  return message
    ? `https://wa.me/${num}?text=${encodeURIComponent(message)}`
    : `https://wa.me/${num}`;
};

// ─── URLs d'exemples pour la documentation API publique ─────────────────────

/** Base URL utilisée dans les exemples de code de la page PortailApi. */
export const API_EXAMPLE_URL: string =
  process.env.REACT_APP_API_EXAMPLE_URL || API_URL;

// ─── Thème « marque blanche » (logo + couleurs par build de tenant) ─────────
//
// Posées par scripts/build-env.js pour les tenants qui fournissent leur propre
// identité visuelle. Le code reste générique : il n'affiche le thème que si
// REACT_APP_BRAND_LOGO est renseigné, et retombe sinon sur le rendu SAGeCom.

/** URL du logo du tenant (chemin relatif à `public/`), ou null. */
const BRAND_LOGO_BRUT: string = process.env.REACT_APP_BRAND_LOGO || '';
export const BRAND_LOGO_URL: string | null = BRAND_LOGO_BRUT
  ? `${process.env.PUBLIC_URL || ''}/${BRAND_LOGO_BRUT.replace(/^\/+/, '')}`
  : null;

/** Raison sociale longue du tenant (sous le logo, pied de page). */
export const BRAND_ORGANISATION: string = process.env.REACT_APP_BRAND_ORGANISATION || '';

/** Accroche courte affichée sur l'écran de connexion. */
export const BRAND_TAGLINE: string = process.env.REACT_APP_BRAND_TAGLINE || '';

/**
 * Domaines métier mis en avant sur le panneau gauche de la connexion.
 * Liste séparée par des points-virgules, vide = section masquée (tenant non
 * configuré : l'écran reste exactement celui d'avant).
 */
export const BRAND_MODULES: string[] = (process.env.REACT_APP_BRAND_MODULES || '')
  .split(';').map((s) => s.trim()).filter(Boolean);

/**
 * Points d'accès de la solution (back-office, portail public, application
 * terrain…). Même convention que BRAND_MODULES.
 */
export const BRAND_ACCES: string[] = (process.env.REACT_APP_BRAND_ACCES || '')
  .split(';').map((s) => s.trim()).filter(Boolean);

/** Couleurs de marque (hex). Défauts = palette SAGeCom. */
export const BRAND_COLORS = {
  primary: process.env.REACT_APP_BRAND_COLOR_PRIMARY || '#2563EB',
  secondary: process.env.REACT_APP_BRAND_COLOR_SECONDARY || '#4338CA',
  light: process.env.REACT_APP_BRAND_COLOR_LIGHT || '#DBEAFE',
};

/**
 * Icône des puces « domaines métier » du panneau marque blanche, désignée par
 * son nom d'export lucide-react (ex. « Car », « Zap », « GraduationCap »).
 *
 * Historiquement l'icône était `Droplet` codée en dur — l'eau du premier
 * tenant (SODECA). `Droplet` reste donc le repli : un build qui ne renseigne
 * pas cette variable rend exactement comme avant.
 */
export const BRAND_MODULES_ICONE: string =
  process.env.REACT_APP_BRAND_MODULES_ICONE || 'Droplet';

/**
 * Vrai quand le build porte une identité de tenant.
 *
 * Octobre 2026 — le critère était « un logo est fourni ». Il écartait quatre
 * instances qui déclarent pourtant leur raison sociale, leur accroche, leurs
 * couleurs et leurs domaines métier, mais n'ont pas de fichier de logo :
 * elles se voyaient proposer le discours générique du progiciel (« Caisse,
 * stock, RH, comptabilité OHADA »), sans rapport avec leur activité.
 *
 * Le panneau de marque sait déjà se passer de logo — le titre porte alors
 * seul l'identité. Une organisation est donc reconnue dès qu'elle est
 * NOMMÉE : raison sociale ou accroche. Un build qui ne renseigne rien garde
 * exactement l'écran qu'il avait.
 */
export const IS_MARQUE_BLANCHE: boolean =
  BRAND_LOGO_URL !== null || BRAND_ORGANISATION !== '' || BRAND_TAGLINE !== '';
